import { useCallback, useEffect, useState } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import usePlacesAutocomplete from "use-places-autocomplete";

interface AutoCompleteInputProps {
  inputId?: string;
  onChange: (selectedOption: any) => void;
  value?: any;
  placeholder?: string;
  onSelect?: () => void;
}

const AutoCompleteInput = ({
  inputId,
  onChange,
  onSelect,
  ...props
}: AutoCompleteInputProps) => {
  const DEBOUNCE_TIME_MS = 300;
  const CALLBACK_NAME = "googleMapsCallback";
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const googlePlacesClientKey = process.env.GOOGLE_PLACES_CLIENT_KEY;

  useEffect(() => {
    const scriptId = "google-maps-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googlePlacesClientKey}&libraries=places&callback=${CALLBACK_NAME}`;
      script.id = scriptId;
      script.async = true;
      script.defer = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () => console.error("Error loading Google Maps script");
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }
  }, [googlePlacesClientKey]);

  const {
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: DEBOUNCE_TIME_MS,
    callbackName: CALLBACK_NAME,
  });

  const loadOptions = useCallback(
    (inputValue: string) => {
      setValue(inputValue);
      return new Promise<Array<{ value: string; label: string }>>((resolve) => {
        setTimeout(() => {
          if (status === "OK") {
            const options = data.map((suggestion) => ({
              value: suggestion.place_id,
              label: suggestion.description,
            }));
            resolve(options);
          } else {
            resolve([]);
          }
        }, DEBOUNCE_TIME_MS);
      });
    },
    [setValue, data, status],
  );

  const handleInputChange = (newValue, { action }) => {
    if (action === "input-change") {
      setValue(newValue);
    }
    return newValue;
  };

  const handleChange = (selectedOption) => {
    clearSuggestions();
    if (onChange) {
      onChange(selectedOption);
      if (onSelect) {
        onSelect();
      }
    }
  };

  const renderCreateOptionLabel = (inputValue: string) => {
    return `Use "${inputValue}"`;
  };

  const createOptionStyling = {
    color: "#0162FF",
    fontSize: "16px",
  };

  return (
    <div className="tw-relative">
      {scriptLoaded && (
        <AsyncCreatableSelect
          inputId={inputId}
          cacheOptions
          defaultOptions
          loadOptions={loadOptions}
          onInputChange={handleInputChange}
          onChange={handleChange}
          formatCreateLabel={renderCreateOptionLabel}
          createOptionPosition="first"
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              height: "64px",
              borderRadius: "6px",
              borderColor: "#e2e8f0",
              boxShadow: "none",
              backgroundColor: baseStyles.backgroundColor,
              "&:hover": {
                borderColor: "#cbd5e1",
              },
            }),
            input: (baseStyles) => ({
              ...baseStyles,
              margin: "0",
              padding: "0",
              fontSize: "18px",
            }),
            placeholder: (baseStyles) => ({
              ...baseStyles,
              color: "#94a3b8",
              fontSize: "18px",
            }),
            singleValue: (baseStyles) => ({
              ...baseStyles,
              fontSize: "18px",
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: state.isFocused ? "#f1f5f9" : "white",
              color: "#334155",
              fontSize: "14px",
              cursor: "pointer",
              ...(state.data.__isNew__ && createOptionStyling),
            }),
            indicatorSeparator: () => ({
              display: "none",
            }),
            dropdownIndicator: () => ({
              display: "none",
            }),
          }}
          classNames={{
            control: () => "tw-bg-white",
          }}
          {...props}
        />
      )}
    </div>
  );
};

export default AutoCompleteInput;
